import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Building2, Users, Send, FileText, Inbox, BarChart3, Settings, LogOut, OctagonPause, Circle, Menu } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard" },
  { to: "/companies", label: "Entreprises", icon: Building2, id: "companies" },
  { to: "/contacts", label: "Contacts", icon: Users, id: "contacts" },
  { to: "/campaigns", label: "Campagnes", icon: Send, id: "campaigns" },
  { to: "/templates", label: "Modèles", icon: FileText, id: "templates" },
  { to: "/inbox", label: "Boîte de réception", icon: Inbox, id: "inbox" },
  { to: "/analytics", label: "Analyses", icon: BarChart3, id: "analytics" },
  { to: "/settings", label: "Paramètres", icon: Settings, id: "settings" },
];

function StatusFooter() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    const load = () => api.get("/dashboard").then((r) => setSt(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("status:refresh", load);
    return () => { clearInterval(t); window.removeEventListener("status:refresh", load); };
  }, []);
  if (!st) return null;
  const ms = st.microsoft?.connected;
  return (
    <div className="space-y-2 text-xs" data-testid="sidebar-status">
      <div className="flex items-center gap-2 text-slate-600">
        <Circle className={`h-2 w-2 ${ms ? "fill-emerald-500 text-emerald-500" : "fill-slate-300 text-slate-300"}`} />
        <span>{ms ? `M365 · ${st.microsoft.account?.email || "connecté"}` : "Microsoft 365 non connecté"}</span>
      </div>
      {st.global_pause && (
        <div className="flex items-center gap-2 text-red-700" data-testid="sidebar-global-pause">
          <OctagonPause className="h-3.5 w-3.5" /> Envois suspendus
        </div>
      )}
      <div className="text-slate-500 tabular">Aujourd'hui : {st.sent_today} / {st.daily_cap}</div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-white">
      {open && <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`w-64 shrink-0 border-r border-slate-200 flex flex-col fixed lg:sticky top-0 left-0 h-screen bg-white z-40 transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="sidebar" onClick={(e) => e.target.closest("a") && setOpen(false)}>
        <div className="px-6 pt-7 pb-8">
          <div className="eyebrow mb-1">Campus Holding SA</div>
          <div className="font-display text-[22px] leading-tight text-[#0b1f3a]">Campus Holding <span className="italic">AI</span></div>
        </div>
        <nav className="flex-1 px-3 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${id}`}
              className={({ isActive }) => `nav-link flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "bg-[#0b1f3a] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-[#0b1f3a]"}`}>
              <Icon className="h-4 w-4" strokeWidth={1.75} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-5 border-t border-slate-200 space-y-4">
          <StatusFooter />
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="text-slate-500 hover:text-[#0b1f3a]" title="Déconnexion">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <span className="font-display text-lg">Campus Holding <span className="italic">AI</span></span>
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-6 lg:py-9 overflow-x-auto">{children}</div>
      </main>
    </div>
  );
}
